import { Fragment, useRef, useState } from 'react';
import { Button } from '../../../../core/components/atoms/Button';
import { Icon } from '../../../../core/components/atoms/Icon';
import { Typography } from '../../../../core/components/molecules/Typography';
import { Toast, toast } from '../../../../core/components/atoms/Toast';
import { useSendEssayImage } from '../../hooks/useSendEssayImage';
import { useUserStore } from '../../../Auth/stores/authStore';
import { Alert } from '../../../../core/components/atoms/Alert';
import { LoadingIcon } from 'morpheus-ui';
import { useBoardConfig } from '../../../../core/hooks/useBoardConfig';
import { useEssayCoreStore } from '../../../../core/stores/essayCoreStore';

interface ISelectImage {
  imageURL?: string;
  setImage: (url: string) => void;
  saveImage: (url: string) => void;
  essaySent: boolean;
  correctionType?: number;
}

export default function SelectImage({
  imageURL,
  setImage,
  saveImage,
  essaySent,
  correctionType
}: ISelectImage) {
  const inputFileRef = useRef<HTMLInputElement>(null);
  const { user } = useUserStore();
  const { setImage: setCoreImage } = useEssayCoreStore();
  const { mutate, isPending: isLoading } = useSendEssayImage((url: string) => {
    setImage(url);
    setCoreImage(url);
    saveImage(url);
  });
  const { board } = useBoardConfig(correctionType || 2);

  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>('');

  const doUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setOpenAlert(false);
    setAlertMessage('');
    const file =
      (event && event.target && event.target.files && event.target.files[0]) ||
      null;

    if (file) {
      const imageRegex = /\.(jpg|jpeg|png)$/;

      if (!imageRegex.test(file.name)) {
        setOpenAlert(true);
        setAlertMessage('foto em formato não suportado.');

        return;
      }

      if (Math.round((file.size * 100) / (1024 * 1024)) / 100 > 5) {
        setOpenAlert(true);
        setAlertMessage('foto excede tamanho permitido.');

        return;
      }

      mutate({ file: file, id: user?.id || '' });
    } else {
      toast({
        variant: 'error',
        message: 'Não foi possivel carregar sua imagem',
        title: 'Erro!',
      });
    }
  };

  const abrirInputFile = () => {
    if (inputFileRef.current && !essaySent) {
      inputFileRef.current.click();
    }
  };

  const openPrintPaper = () => {
    window.open(board.paper, '_blank');
  }

  return (
    <Fragment>
      <Toast />
      <div className='flex w-full justify-between xs:mb-12 xs:flex-col-reverse xs:gap-xxl md:items-center lg:flex-row'>
        <div>
          <Typography>Garanta a legibilidade do seu texto!</Typography>
          <Typography>
            <b>Enquadre</b> bem a folha com sua redação e tire a foto em um
            <b> ambiente com boa iluminação</b>.
          </Typography>
        </div>
        <Button size='small' variant='text' onClick={() => openPrintPaper()}>
          <Icon Icon='print' type='material' variant='rounded' />
          Imprimir folha de redação
        </Button>
      </div>
      <div className='flex flex-col gap-xxl'>
        {openAlert && (
          <Alert
            variant='error'
            message={
              <Typography>
                {' '}
                <b>Atenção: </b>
                {alertMessage}{' '}
              </Typography>
            }
          />
        )}
        {isLoading ? (
          <div className='flex w-full items-center justify-center'>
            <LoadingIcon className='h-6 animate-spin fill-none stroke-current text-center' />
          </div>
        ) : (
          <Fragment>
            {!imageURL ? (
              <div className='border-mono-color-darkest flex w-full flex-col items-center gap-xxl rounded-md border-hairline border-dashed px-0 py-12'>
                <Icon
                  type='material'
                  Icon='add_photo_alternate'
                  className='text-mono-color-darkest text-5xl'
                />
                <div className='flex flex-col gap-sm'>
                  <Button className='m-auto' onClick={() => abrirInputFile()}>
                    <Icon
                      Icon='upgrade'
                      type='material'
                      variant='rounded'
                      className='xs:hidden lg:block'
                    />
                    Selecionar foto de redação
                  </Button>
                  <Typography size='small' className='text-center'>
                    IMPORTANTE: A foto deve ser em formato <br />
                    <b>.png</b> ou <b>.jpeg</b>, com no máximo <b>5MB</b>.
                  </Typography>
                </div>
              </div>
            ) : (
              <div className='flex flex-col gap-xxl'>
                <div className='flex flex-col gap-sm'>
                  <Button
                    variant='outlined'
                    LeftIcon='upload_2'
                    iconType='material'
                    full
                    onClick={() => abrirInputFile()}
                  >
                    <Typography>Trocar imagem</Typography>
                  </Button>
                  <Typography size='mini' className='text-center'>
                    IMPORTANTE: A foto deve ser em formato <b>.png</b> ou{' '}
                    <b>.jpeg</b> com no máximo <b>5MB</b>.
                  </Typography>
                </div>
                {!openAlert && (
                  <Fragment>
                    <Typography
                      size='mini'
                      variant='italic'
                      className='mb-2 text-left'
                    >
                      Redação salva
                    </Typography>
                    <img src={imageURL} />
                  </Fragment>
                )}
              </div>
            )}
            <input
              ref={inputFileRef}
              type='file'
              accept='image/*'
              onChange={doUpload}
              className='hidden'
            />
          </Fragment>
        )}
      </div>
    </Fragment>
  );
}
